import React from 'react';
import { Form, Radio } from 'antd';
import { I18n } from '@aws-amplify/core';
import type { RadioChangeEvent } from 'antd';

import { SLIDER_MARKS } from './constants';
import { DefaultQuestionProps } from './types';
import {
	RadioOptionsContainer,
	StyledPerformanceText,
	RadioOptionItem,
	SliderLabels,
	CustomSlider,
	SliderLabel,
	StyledTitle,
	StyledText
} from './styles';

export function DefaultQuestion({ question, isMobile, saveAnswer }: DefaultQuestionProps) {
	const form = Form.useFormInstance();

	const fieldName = `question_${question.id}`;
	const options = question.options ?? [];

	function handleRadioChange(e: RadioChangeEvent): void {
		const value = e.target.value;
		form.setFieldValue(fieldName, value);
		if (saveAnswer) {
			const opt = options.find((o) => o.value === value);
			if (opt) saveAnswer(question.id, opt.id);
		}
	}

	function handleSliderAfterChange(value: number | undefined | null): void {
		const valueToSet = (value === undefined || value === null) && SLIDER_MARKS[0] === '0' ? 0 : value;

		if (valueToSet !== undefined && valueToSet !== null) {
			form.setFieldValue(fieldName, valueToSet);
			if (saveAnswer) {
				saveAnswer(question.id, String(valueToSet));
			}
		} else {
			form.setFieldValue(fieldName, undefined);
		}
	}

	return (
		<div style={{ marginTop: 24 }}>
			<Form.Item
				label={
					<div>
						<StyledTitle level={4}>{question.title}</StyledTitle>
						<StyledText>{I18n.get(question.description)}</StyledText>
						<StyledPerformanceText hidden={question.category !== 'performance'}>
							{I18n.get('Attention: the scale in this question has a different format than the others')}
						</StyledPerformanceText>
					</div>
				}
			>
				<Form.Item
					rules={[{ required: true, message: I18n.get('Please fill this field') }]}
					validateTrigger={['onChange', 'onBlur', 'onSubmit']}
					name={fieldName}
					preserve={true}
					noStyle
				>
					{options.length > 0 ? (
						<Radio.Group
							style={{ width: '100%' }}
							onChange={(e) => {
								form.setFieldValue(fieldName, e.target.value);
								handleRadioChange(e);
							}}
						>
							<RadioOptionsContainer>
								{options.map((option) => (
									<RadioOptionItem key={option.id}>
										<Radio value={option.value}>{I18n.get(option.name)}</Radio>
									</RadioOptionItem>
								))}
							</RadioOptionsContainer>
						</Radio.Group>
					) : (
						<CustomSlider
							$value={form.getFieldValue(fieldName)}
							onAfterChange={handleSliderAfterChange}
							marks={isMobile ? {} : SLIDER_MARKS}
							max={100}
							step={5}
							min={0}
						/>
					)}
				</Form.Item>
				{!options.length && (
					<SliderLabels>
						<SliderLabel>
							{question.category !== 'performance' ? I18n.get('Low') : I18n.get('Very')}
						</SliderLabel>
						<SliderLabel>
							{question.category !== 'performance' ? I18n.get('High') : I18n.get('Little')}
						</SliderLabel>
					</SliderLabels>
				)}
			</Form.Item>
		</div>
	);
}
